import React, { useState, useEffect } from "react";
import {
  Alert,
  Button,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
} from "@mui/material";

import { useData } from "../contexts/DataContext"; // Import DataContext
import { useDualMsal } from "../contexts/MsalContext"; // Import useDualMsal hook
import {
  processFailedUploads,
  FAILED_UPLOADS_KEY,
} from "../utilities/postData"; // Import processFailedUploads and FAILED_UPLOADS_KEY

const CachedUploadsNotification = () => {
  // Get participant data and auth functions from context
  const { participant } = useData();
  const { getToken } = useDualMsal(); // Use the hook to get getToken

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [cachedUploads, setCachedUploads] = useState([]);
  const [isRetrying, setIsRetrying] = useState(false);

  // Check for cached uploads on component mount
  useEffect(() => {
    // Only check if participant ID is available
    if (participant?.id) {
      checkForCachedUploads();
      // Re-check every 30 seconds
      const interval = setInterval(checkForCachedUploads, 30000);
      return () => clearInterval(interval);
    }
  }, [participant?.id]); // Rerun when participant ID changes

  // Function to check localStorage for cached uploads for the current participant
  const checkForCachedUploads = () => {
    if (!participant?.id) return; // Don't check if no participant ID

    try {
      const allCachedData = JSON.parse(
        localStorage.getItem(FAILED_UPLOADS_KEY) || "[]"
      );
      // Filter uploads for the current participant
      const userCachedData = allCachedData.filter(
        (upload) => upload.participantId === participant.id
      );
      setCachedUploads(userCachedData);

      // Only show notification if the current user has cached uploads
      if (userCachedData.length > 0) {
        setOpenSnackbar(true);
      } else {
        setOpenSnackbar(false);
      }
    } catch (error) {
      console.error("Error checking for cached uploads:", error);
    }
  };

  // Handle retry button click
  const handleRetry = async () => {
    if (!participant?.id || !getToken) {
      // Check for getToken
      console.error(
        "Cannot retry: Missing participant ID or getToken function." // Updated error message
      );
      return;
    }
    setIsRetrying(true);
    try {
      // Acquire the current access token using getToken
      const accessToken = await getToken(); // Use getToken
      if (!accessToken) {
        throw new Error("Failed to acquire access token for retry.");
      }
      // Call processFailedUploads with the current token
      await processFailedUploads(accessToken);
      // Re-check to update the count (will automatically filter for user)
      checkForCachedUploads();
    } catch (error) {
      console.error("Error retrying uploads:", error);
      // Optionally show an error message to the user
    } finally {
      setIsRetrying(false);
      setOpenDialog(false);
    }
  };

  // Format date for display
  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  return (
    <>
      {/* Snackbar notification */}
      <Snackbar
        open={openSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="warning"
          sx={{ width: "100%" }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => setOpenDialog(true)}
            >
              VIEW
            </Button>
          }
        >
          You have {cachedUploads.length} unsent task data{" "}
          {cachedUploads.length === 1 ? "submission" : "submissions"} stored
          locally.
        </Alert>
      </Snackbar>

      {/* Detailed dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Pending Data Uploads</DialogTitle>
        <DialogContent>
          <Typography paragraph>
            The following data submissions couldn't be sent to the server and
            are stored on your device. You can retry sending them now.
          </Typography>

          <List>
            {cachedUploads.map((upload, index) => (
              <React.Fragment key={upload.id || index}>
                <ListItem>
                  <ListItemText
                    primary={`${upload.data?.session || "Unknown session"} - ${
                      upload.data?.module || "Unknown module"
                    }`}
                    secondary={`From: ${formatDate(upload.timestamp)}`}
                  />
                </ListItem>
                {index < cachedUploads.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} color="primary">
            Close
          </Button>
          <Button
            onClick={handleRetry}
            color="primary"
            variant="contained"
            disabled={isRetrying}
          >
            {isRetrying ? "Retrying..." : "Retry All Now"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CachedUploadsNotification;
